import { useState, useEffect } from 'react'
import { useAuth } from '../lib/AuthContext'
import { supabase, getUserCompanyId } from '../lib/supabase'

const STATUS = {
  on_track:  { label: 'On track',  color: '#166534', bg: '#F0FDF4', border: '#86EFAC', dot: '#22C55E' },
  off_track: { label: 'Off track', color: '#991B1B', bg: '#FEF2F2', border: '#FCA5A5', dot: '#EF4444' },
  complete:  { label: 'Complete',  color: '#1E3A8A', bg: '#EFF6FF', border: '#93C5FD', dot: '#3B82F6' },
}

function getCurrentQuarter() {
  const now = new Date()
  const q = Math.ceil((now.getMonth() + 1) / 3)
  return `Q${q} ${now.getFullYear()}`
}

function getQuarterOptions() {
  const now = new Date()
  const year = now.getFullYear()
  const q = Math.ceil((now.getMonth() + 1) / 3)
  const options = []
  // 2 past quarters, current, 2 future
  for (let i = -2; i <= 2; i++) {
    let qn = q + i
    let yn = year
    while (qn < 1) { qn += 4; yn-- }
    while (qn > 4) { qn -= 4; yn++ }
    options.push(`Q${qn} ${yn}`)
  }
  return options
}

const DEFAULT_DATA = { quarters: {} }

let nid = 200

function emptyQuarter() {
  return {
    companyRocks: [],
    individuals: []
  }
}

async function loadPriorities(companyId) {
  if (!companyId) return null
  const { data } = await supabase.from('priorities').select('*').eq('company_id', companyId).maybeSingle()
  return data
}

async function savePriorities(companyId, data) {
  if (!companyId) return
  const { data: { session } } = await supabase.auth.getSession()
  const userId = session?.user?.id
  await supabase.from('priorities').upsert(
    { company_id: companyId, user_id: userId, priorities_data: data, updated_at: new Date().toISOString() },
    { onConflict: 'company_id' }
  )
}

export default function Priorities({ previewUserId }) {
  const { user: authUser } = useAuth()
  const user = previewUserId ? { ...authUser, id: previewUserId } : authUser
  const [data, setData] = useState(null)
  const [quarter, setQuarter] = useState(getCurrentQuarter())
  const [loading, setLoading] = useState(true)
  const [saveStatus, setSaveStatus] = useState('saved')
  const [dbReady, setDbReady] = useState(true)
  const [editingId, setEditingId] = useState(null)
  const [companyId, setCompanyId] = useState(previewUserId || null)
  const [companyIdResolved, setCompanyIdResolved] = useState(!!previewUserId)

  // Mobile detection
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  useEffect(() => {
    if (previewUserId) { setCompanyId(previewUserId); setCompanyIdResolved(true); return }
    if (!authUser?.email) return
    let alive = true
    getUserCompanyId(authUser.email).then(cid => { if (!alive) return; setCompanyId(cid); setCompanyIdResolved(true) })
    return () => { alive = false }
  }, [authUser?.email, previewUserId])

  useEffect(() => {
    async function load() {
      if (!user || !companyIdResolved) return
      try {
        const result = await loadPriorities(companyId)
        setData(result?.priorities_data || JSON.parse(JSON.stringify(DEFAULT_DATA)))
      } catch {
        setData(JSON.parse(JSON.stringify(DEFAULT_DATA)))
        setDbReady(false)
      }
      setLoading(false)
    }
    load()
  }, [user, companyId, companyIdResolved])

  useEffect(() => {
    if (!data || loading || !dbReady) return
    if (previewUserId) return // read-only in admin preview
    if (!companyId) return
    setSaveStatus('unsaved')
    const t = setTimeout(async () => {
      setSaveStatus('saving')
      await savePriorities(companyId, data)
      setSaveStatus('saved')
    }, 2000)
    return () => clearTimeout(t)
  }, [data]) // eslint-disable-line

  function updateData(fn) {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev))
      fn(next)
      return next
    })
  }

  function getQ() {
    if (!data) return emptyQuarter()
    return data.quarters[quarter] || emptyQuarter()
  }

  function ensureQ(d) {
    if (!d.quarters[quarter]) d.quarters[quarter] = emptyQuarter()
  }

  function addCompanyRock() {
    updateData(d => {
      ensureQ(d)
      d.quarters[quarter].companyRocks.push({ id: nid++, title: 'New company priority', owner: '', status: 'on_track' })
    })
  }

  function updateCompanyRock(id, field, val) {
    updateData(d => {
      ensureQ(d)
      const r = d.quarters[quarter].companyRocks.find(r => r.id === id)
      if (r) r[field] = val
    })
  }

  function deleteCompanyRock(id) {
    updateData(d => {
      ensureQ(d)
      d.quarters[quarter].companyRocks = d.quarters[quarter].companyRocks.filter(r => r.id !== id)
    })
  }

  function addIndividual() {
    updateData(d => {
      ensureQ(d)
      d.quarters[quarter].individuals.push({ id: nid++, name: 'New person', rocks: [] })
    })
  }

  function updateIndividualName(id, val) {
    updateData(d => {
      ensureQ(d)
      const p = d.quarters[quarter].individuals.find(p => p.id === id)
      if (p) p.name = val
    })
  }

  function deleteIndividual(id) {
    updateData(d => {
      ensureQ(d)
      d.quarters[quarter].individuals = d.quarters[quarter].individuals.filter(p => p.id !== id)
    })
  }

  function addRock(personId) {
    updateData(d => {
      ensureQ(d)
      const p = d.quarters[quarter].individuals.find(p => p.id === personId)
      if (p) p.rocks.push({ id: nid++, title: 'New priority', status: 'on_track' })
    })
  }

  function updateRock(personId, rockId, field, val) {
    updateData(d => {
      ensureQ(d)
      const p = d.quarters[quarter].individuals.find(p => p.id === personId)
      if (!p) return
      const r = p.rocks.find(r => r.id === rockId)
      if (r) r[field] = val
    })
  }

  function deleteRock(personId, rockId) {
    updateData(d => {
      ensureQ(d)
      const p = d.quarters[quarter].individuals.find(p => p.id === personId)
      if (p) p.rocks = p.rocks.filter(r => r.id !== rockId)
    })
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ fontSize: 14, color: '#888' }}>Loading priorities…</div>
    </div>
  )

  const q = getQ()
  const quarters = getQuarterOptions()

  return (
    <div style={{ padding: isMobile ? '20px 14px' : 32, maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', marginBottom: isMobile ? 24 : 32, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
          <h1 style={{ fontSize: isMobile ? 20 : 22, fontWeight: 600, color: '#2C2C2A', marginBottom: 4 }}>90 Day Priorities</h1>
          <div style={{ fontSize: 12, color: '#888780' }}>Company and individual priorities by quarter</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: saveStatus === 'saved' ? '#3B6D11' : saveStatus === 'saving' ? '#854F0B' : '#888', whiteSpace: 'nowrap' }}>
            {saveStatus === 'saved' && '✓ Saved'}
            {saveStatus === 'saving' && '↑ Saving…'}
            {saveStatus === 'unsaved' && '● Unsaved'}
          </span>
          <select
            value={quarter}
            onChange={e => setQuarter(e.target.value)}
            style={{ fontSize: 14, fontWeight: 600, border: '1px solid #D0CDC7', borderRadius: 6, padding: '6px 12px', background: '#fff', color: '#2C2C2A', cursor: 'pointer' }}
          >
            {quarters.map(q => <option key={q} value={q}>{q}</option>)}
          </select>
        </div>
      </div>

      {!dbReady && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '14px 18px', marginBottom: 24, fontSize: 13, color: '#991B1B', lineHeight: 1.6 }}>
          <strong>One more setup step needed.</strong> Go to Supabase → SQL Editor and run the priorities table setup SQL provided by your consultant.
        </div>
      )}

      {/* Company Rocks */}
      <div style={{ marginBottom: 40 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 3, height: 20, background: '#1E3320', borderRadius: 2 }} />
            <h2 style={{ fontSize: 16, fontWeight: 600, color: '#2C2C2A' }}>Company priorities</h2>
            <span style={{ fontSize: 11, color: '#888780', background: '#F1EFE8', padding: '2px 8px', borderRadius: 999 }}>{q.companyRocks.length} rocks</span>
          </div>
          <button onClick={addCompanyRock} style={addBtnStyle}>+ Add</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {q.companyRocks.length === 0 && (
            <div style={{ fontSize: 13, color: '#aaa', padding: '20px 0', textAlign: 'center', background: '#F9F7F4', borderRadius: 8, border: '1px dashed #D0CDC7' }}>
              No company priorities for {quarter} yet
            </div>
          )}
          {q.companyRocks.map(rock => (
            <RockRow
              key={rock.id}
              rock={rock}
              editingId={editingId}
              setEditingId={setEditingId}
              onUpdate={(field, val) => updateCompanyRock(rock.id, field, val)}
              onDelete={() => deleteCompanyRock(rock.id)}
              showOwner={true}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>

      {/* Individual Rocks */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 3, height: 20, background: '#3B6D11', borderRadius: 2 }} />
            <h2 style={{ fontSize: 16, fontWeight: 600, color: '#2C2C2A' }}>Individual priorities</h2>
          </div>
          <button onClick={addIndividual} style={addBtnStyle}>+ Add person</button>
        </div>

        {q.individuals.length === 0 && (
          <div style={{ fontSize: 13, color: '#aaa', padding: '20px 0', textAlign: 'center', background: '#F9F7F4', borderRadius: 8, border: '1px dashed #D0CDC7' }}>
            No individual priorities for {quarter} yet
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {q.individuals.map(person => (
            <div key={person.id} style={{ background: '#FAFAF8', border: '1px solid #EBE7DE', borderRadius: 10, overflow: 'hidden' }}>
              {/* Person header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: isMobile ? '12px 14px' : '14px 18px', background: '#F1EFE8', borderBottom: '1px solid #EBE7DE', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '1 1 auto' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#1E3320', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: '#fff', flexShrink: 0 }}>
                    {person.name.charAt(0).toUpperCase()}
                  </div>
                  {editingId === `person-${person.id}` ? (
                    <input
                      autoFocus
                      value={person.name}
                      onChange={e => updateIndividualName(person.id, e.target.value)}
                      onBlur={() => setEditingId(null)}
                      style={{ fontSize: 15, fontWeight: 600, border: '1px solid #3B6D11', borderRadius: 4, padding: '3px 8px', outline: 'none', background: '#fff', minWidth: 0, flex: '1 1 auto' }}
                    />
                  ) : (
                    <span onClick={() => setEditingId(`person-${person.id}`)} style={{ fontSize: 15, fontWeight: 600, color: '#2C2C2A', cursor: 'text' }}>{person.name}</span>
                  )}
                  <span style={{ fontSize: 11, color: '#888780', flexShrink: 0 }}>{person.rocks.length} {person.rocks.length === 1 ? 'priority' : 'priorities'}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => addRock(person.id)} style={addBtnStyle}>+ Add</button>
                  <button onClick={() => deleteIndividual(person.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ccc', fontSize: 18, padding: '4px 8px', lineHeight: 1 }}>×</button>
                </div>
              </div>
              {/* Rocks */}
              <div style={{ padding: isMobile ? '10px 14px' : '12px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {person.rocks.length === 0 && (
                  <div style={{ fontSize: 12, color: '#aaa', textAlign: 'center', padding: '12px 0' }}>No priorities yet — tap "+ Add"</div>
                )}
                {person.rocks.map(rock => (
                  <RockRow
                    key={rock.id}
                    rock={rock}
                    editingId={editingId}
                    setEditingId={setEditingId}
                    onUpdate={(field, val) => updateRock(person.id, rock.id, field, val)}
                    onDelete={() => deleteRock(person.id, rock.id)}
                    isMobile={isMobile}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 32, flexWrap: 'wrap' }}>
        {Object.entries(STATUS).map(([key, s]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#888780' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot }} />
            {s.label}
          </div>
        ))}
        <div style={{ fontSize: 11, color: '#aaa', marginLeft: 'auto' }}>Click any text to edit · Changes save automatically</div>
      </div>
    </div>
  )
}

function RockRow({ rock, editingId, setEditingId, onUpdate, onDelete, showOwner, isMobile }) {
  const s = STATUS[rock.status || 'on_track']

  // Title element shared across desktop + mobile
  const titleEl = editingId === `rock-${rock.id}` ? (
    <input
      autoFocus
      value={rock.title}
      onChange={e => onUpdate('title', e.target.value)}
      onBlur={() => setEditingId(null)}
      style={{ flex: 1, fontSize: 14, border: '1px solid #3B6D11', borderRadius: 4, padding: '5px 8px', outline: 'none', background: '#fff', width: '100%', boxSizing: 'border-box' }}
    />
  ) : (
    <span
      onClick={() => setEditingId(`rock-${rock.id}`)}
      style={{
        flex: 1, fontSize: 14, lineHeight: 1.45,
        color: rock.status === 'complete' ? '#888' : '#2C2C2A',
        cursor: 'text',
        textDecoration: rock.status === 'complete' ? 'line-through' : 'none'
      }}
    >
      {rock.title}
    </span>
  )

  const statusEl = (
    <select
      value={rock.status || 'on_track'}
      onChange={e => onUpdate('status', e.target.value)}
      style={{ fontSize: 12, border: `1px solid ${s.border}`, borderRadius: 4, padding: '5px 10px', background: s.bg, color: s.color, cursor: 'pointer', flexShrink: 0, fontWeight: 500 }}
    >
      <option value="on_track">On track</option>
      <option value="off_track">Off track</option>
      <option value="complete">Complete</option>
    </select>
  )

  const ownerEl = showOwner && (
    editingId === `owner-${rock.id}` ? (
      <input
        autoFocus
        value={rock.owner || ''}
        onChange={e => onUpdate('owner', e.target.value)}
        onBlur={() => setEditingId(null)}
        placeholder="Owner"
        style={{ flex: isMobile ? '1 1 auto' : '0 0 auto', width: isMobile ? 'auto' : 100, fontSize: 13, border: '1px solid #3B6D11', borderRadius: 4, padding: '5px 8px', outline: 'none', background: '#fff' }}
      />
    ) : (
      <span
        onClick={() => setEditingId(`owner-${rock.id}`)}
        style={{ fontSize: 13, color: rock.owner ? '#4A4840' : '#bbb', cursor: 'text', whiteSpace: 'nowrap', flexShrink: 0, minWidth: isMobile ? 0 : 80, textAlign: isMobile ? 'left' : 'right', padding: isMobile ? '4px 0' : 0 }}
      >
        {rock.owner || 'Add owner'}
      </span>
    )
  )

  const deleteEl = (
    <button onClick={onDelete} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ccc', fontSize: 18, padding: 4, flexShrink: 0, lineHeight: 1, marginLeft: 'auto' }}>×</button>
  )

  if (isMobile) {
    return (
      <div style={{ background: '#fff', border: '1px solid #EBE7DE', borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.dot, flexShrink: 0, marginTop: 6 }} />
          {titleEl}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingLeft: 20, flexWrap: 'wrap' }}>
          {statusEl}
          {ownerEl}
          {deleteEl}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #EBE7DE', borderRadius: 8, padding: '10px 14px' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {titleEl}
      {statusEl}
      {ownerEl}
      {deleteEl}
    </div>
  )
}

const addBtnStyle = {
  fontSize: 11, padding: '5px 12px', borderRadius: 6,
  border: '1px solid #D0CDC7', background: 'transparent',
  color: '#4A4840', cursor: 'pointer', fontWeight: 400
}
